import type { Component } from 'vue'
import { computed, markRaw, ref } from 'vue'

// Global registry instance
const rendererRegistry = ref<Map<string, Component>>(new Map())
const isInitialized = ref(false)

/**
 * Auto-discovery of renderer components
 */
const discoverRenderers = async (): Promise<void> => {
  try {
    // Use import.meta.glob to discover renderer component files
    const rendererModules = import.meta.glob('/src/composites/NotificationRenderers/*.vue', { eager: false })

    for (const [path, moduleImporter] of Object.entries(rendererModules)) {
      try {
        const module = (await moduleImporter()) as { default: Component }
        const component = module.default

        // Extract component name from file path (remove directory and extension)
        const componentName = path.split('/').pop()?.replace('.vue', '') || ''

        if (componentName && component) {
          register(componentName, component)
          console.log(`Loaded notification renderer: ${componentName} from: ${path}`)
        }
      } catch (error) {
        console.warn(`Failed to load notification renderer from ${path}:`, error)
      }
    }
  } catch (error) {
    console.warn('Failed to discover notification renderers:', error)
  }
}

/**
 * Register a single renderer component
 */
const register = (rendererName: string, component: Component): void => {
  rendererRegistry.value.set(rendererName, markRaw(component))
}

/**
 * Get a renderer component by name
 */
const get = (rendererName: string): Component | undefined => {
  return rendererRegistry.value.get(rendererName)
}

/**
 * Check if a renderer exists
 */
const has = (rendererName: string): boolean => {
  return rendererRegistry.value.has(rendererName)
}

/**
 * Get all registered renderer names
 */
const getRendererNames = (): string[] => {
  return Array.from(rendererRegistry.value.keys())
}

/**
 * Clear all registered renderers
 */
const clear = (): void => {
  rendererRegistry.value.clear()
  isInitialized.value = false
}

/**
 * Initialize the renderer registry
 */
const initialize = async (): Promise<void> => {
  if (isInitialized.value) return

  try {
    console.log('Initializing notification renderer registry...')

    // Discover and register all renderers
    await discoverRenderers()

    isInitialized.value = true
    console.log(`Notification renderer registry initialized with ${rendererRegistry.value.size} renderers`)
  } catch (error) {
    console.error('Failed to initialize notification renderer registry:', error)
  }
}

/**
 * Main composable for notification renderer registry
 */
export function useRendererRegistry() {
  return {
    // State
    isInitialized: computed(() => isInitialized.value),
    rendererCount: computed(() => rendererRegistry.value.size),

    // Registry methods
    register,
    get,
    has,
    getRendererNames,
    clear,
    initialize,

    // Registry interface
    registry: {
      register,
      get,
      has,
      getRendererNames,
      clear,
    } as RendererRegistry,
  }
}

// Type definitions
export interface RendererRegistry {
  register: (rendererName: string, component: Component) => void
  get: (rendererName: string) => Component | undefined
  has: (rendererName: string) => boolean
  getRendererNames: () => string[]
  clear: () => void
}
